import * as R from "remeda";
import z from "zod";

const some = <T>(v: T | null | undefined): v is T => v !== undefined && v !== null;

/* Upload Tokens -------------------------------------------------------------*/

export const UploadTokenSchema = z.object({
  url: z.url(),
  headers: z.record(z.string(), z.string()),
  key: z.string(),
});

export type UploadToken = z.infer<typeof UploadTokenSchema>;

/* Studies -------------------------------------------------------------------*/

export const StudyIDSchema = z
  .string()
  .trim()
  .regex(/^[a-zA-Z0-9-]+$/, "Invalid ID")
  .min(1, "Required")
  .min(5, "Too short");

export const ParticipantIDSchema = z
  .string()
  .trim()
  .regex(/^[a-zA-Z0-9-]*$/, "Invalid ID")
  .min(5, "Too short");

// Study Base

export const StudyFlags = z.object({
  lowMessageVolume: z
    .object({
      enabled: z.boolean(),
      perWeekThreshold: z.number().min(1).optional(),
      staticThreshold: z.number().min(1).optional(),
    })
    .refine((v) => (v.perWeekThreshold === undefined) !== (v.staticThreshold === undefined), {
      message: "Specify exactly one of perWeekThreshold or staticThreshold",
    })
    .optional(),
});

export const StudyBaseSchema = z.object({
  id: StudyIDSchema,
  participantId: ParticipantIDSchema.nullish(),
  participantCode: z.string().nullish(),
  enrolledAt: z.iso.datetime().nullish(),
  baselineChatHistoryWindowDays: z.number().min(1).nullish(),
  expiresInSeconds: z.number().min(1).nullish(),
  showReport: z.boolean().optional(),
  skipValidation: z.boolean().optional(),
  overrideMain: z.boolean().optional(),
  flags: StudyFlags.optional(),
});

// Study Configuration

export const StudyConfigSchema = StudyBaseSchema.extend({
  id: StudyIDSchema.optional(),
  studyId: StudyIDSchema.optional(), // Legacy config
  autoCreateMain: z.boolean().nullish(),
})
  .refine((s) => some(s.id) || some(s.studyId), {
    error: "id is required",
    path: ["id"],
  })
  .refine((s) => !s.id || !s.studyId || s.id === s.studyId, {
    error: "id and studyId must match when both are given",
    path: ["studyId"],
  })
  .refine((study) => !(!study.participantId && some(study.autoCreateMain)), {
    error: "participantId must be present to define autoCreateMain",
    path: ["participantId"],
  })
  .refine((study) => !(!study.participantId && some(study.participantCode)), {
    error: "participantId must be present to define participantCode",
    path: ["participantId"],
  })
  .refine((study) => !(!study.participantId && some(study.enrolledAt)), {
    error: "participantId must be present to define enrolledAt",
    path: ["participantId"],
  })
  .refine((study) => !(!study.participantId && some(study.overrideMain)), {
    error: "participantId must be present to define overrideMain",
    path: ["participantId"],
  })
  .transform(({ studyId, ...s }) => ({ ...s, id: (s.id ?? studyId)! }));

export type StudyConfig = z.infer<typeof StudyConfigSchema>;

// Study (Database Entity)

export const StudySchema = StudyBaseSchema.extend({
  key: z.string(),
  createdAt: z.iso.datetime(),
  closed: z.boolean().optional(),
  submissionCount: z.number().optional(),
  closedAt: z.iso.datetime().optional(),
  openedAt: z.iso.datetime().optional(),
});

export type Study = z.infer<typeof StudySchema>;

// Linked Study

export const LinkedMainStudySchema = StudySchema.omit({ key: true, submissionCount: true });

export type LinkedMainStudy = z.infer<typeof LinkedMainStudySchema>;

export const LinkedStudySchema = StudySchema.extend({
  mainStudy: LinkedMainStudySchema.nullable(),
});

export type LinkedStudy = z.infer<typeof LinkedStudySchema>;

// Partial Study (For Updates)

export const StudyUpdateSchema = StudyBaseSchema.omit({
  id: true,
  participantId: true,
})
  .extend({ key: z.string() })
  .strict();

export type StudyUpdate = z.infer<typeof StudyUpdateSchema>;

// Generated Study (API Return)

export const GeneratedStudySchema = StudySchema.extend({
  url: z.url(),
  expiresAt: z.iso.datetime().nullable(),
});

export type GeneratedStudy = z.infer<typeof GeneratedStudySchema>;

// Study Group

export type StudyGroup = {
  id: string;
  mainStudy: Study;
  participants: Study[];
  totalSubmissions: number;
};

/* Submissions ---------------------------------------------------------------*/

// Submission Status

export const SubmissionStatusSchema = z.enum(["pending", "accepted", "rejected"]);

export type SubmissionStatus = z.infer<typeof SubmissionStatusSchema>;

// Submission (Database Entity)

export const SubmissionSchema = z.object({
  id: z.string().min(1),
  studyId: z.string().min(1),
  studyKey: z.string().min(1),
  participantId: z.string().nullish(),
  participantCode: z.string().nullish(),
  timestamp: z.iso.datetime(),
  source: z.string(),
  validated: z.boolean().nullish(),
  truncated: z.boolean().nullish(),
  historySince: z.iso.datetime().nullish(),
  week: z.number().nullish(),
  baselineDays: z.number().nullish(),
  file: z.string().min(1),
  size: z.number(),
  updatedAt: z.iso.datetime().nullish(),
  status: SubmissionStatusSchema.nullish(),
});

export type Submission = z.infer<typeof SubmissionSchema>;

export const SubmissionsSchema = z.array(SubmissionSchema);

export type Submissions = z.infer<typeof SubmissionsSchema>;

export const CompatSubmissionSchema = SubmissionSchema.transform((s) => ({
  ...s,
  submissionId: s.id,
}));

export type CompatSubmission = z.infer<typeof CompatSubmissionSchema>;

export const CompatSubmissionsSchema = z.array(CompatSubmissionSchema);

export type CompatSubmissions = z.infer<typeof CompatSubmissionsSchema>;

/* Submission Reports --------------------------------------------------------*/

export const SubmissionReportDataSchema = z.object({
  userMessageCount: z.number(),
  oldestUserMessageAt: z.iso.datetime().nullish(),
  newestUserMessageAt: z.iso.datetime().nullish(),
});

export type SubmissionReportData = z.infer<typeof SubmissionReportDataSchema>;

export const SubmissionReportSchema = SubmissionReportDataSchema.extend({
  submissionId: z.uuid(),
  studyId: z.string().min(1),
  updatedAt: z.iso.datetime().nullish(),
});

export type SubmissionReport = z.infer<typeof SubmissionReportSchema>;

export const SubmissionReportsSchema = z.array(SubmissionReportSchema);

export type SubmissionReports = z.infer<typeof SubmissionReportsSchema>;

/* Submissions With Report ---------------------------------------------------*/

export const SubmissionWithReportSchema = SubmissionSchema.extend({
  report: SubmissionReportSchema.nullable(),
}).transform((s) => ({
  ...s,
  submissionId: s.id,
}));

export type SubmissionWithReport = z.infer<typeof SubmissionWithReportSchema>;

export const SubmissionsWithReportSchema = z.array(SubmissionWithReportSchema);

export type SubmissionsWithReport = z.infer<typeof SubmissionsWithReportSchema>;

/* Submission Object Metadata ------------------------------------------------*/

function optionalStrToNum(s: string | undefined, ctx: z.RefinementCtx) {
  if (s === undefined || s.trim() === "") return undefined;
  const n = Number(s);
  if (isNaN(n)) {
    ctx.addIssue({ code: "custom", message: "Must be a number" });
    return z.NEVER;
  }
  return n;
}

const OptionalNumStrSchema = z.string().optional().transform(optionalStrToNum);

const OptionalBoolStrSchema = z
  .union([
    z.literal("").transform(() => undefined),
    z.literal("true").transform(() => true),
    z.literal("false").transform(() => false),
  ])
  .optional();

const OptionalDateStrSchema = z
  .union([z.literal("").transform(() => undefined), z.iso.datetime()])
  .optional();

export const SubmissionObjectMetadataSchema = z.object({
  "study-id": z.string().min(1),
  "study-study-id": z.string().min(1).optional(),
  "study-key": z.string().min(1).optional(),
  "study-participant-id": z
    .string()
    .optional()
    .transform((s) => s?.trim() || undefined),
  "study-participant-code": z
    .string()
    .optional()
    .transform((s) => s?.trim() || undefined),
  "submission-id": z.uuid(),
  "submission-history-since": OptionalDateStrSchema,
  "submission-validated": OptionalBoolStrSchema,
  "submission-truncated": OptionalBoolStrSchema,
  "submission-baseline-days": OptionalNumStrSchema,
  "submission-week": OptionalNumStrSchema,
  "submission-source": z.string(),
  "submission-status": SubmissionStatusSchema.optional(),
  "report-user-message-count": OptionalNumStrSchema,
  "report-oldest-user-message-at": OptionalDateStrSchema,
  "report-newest-user-message-at": OptionalDateStrSchema,
});

export type SubmissionObjectMetadata = z.infer<typeof SubmissionObjectMetadataSchema>;

/* Production Study IDs ------------------------------------------------------*/

export const studyIds = ["msm-1a-2", "msm-1a-3"] as const;

export type StudyId = (typeof studyIds)[number];

export function isValidStaticStudyId(studyId: string | null): studyId is StudyId {
  return studyId !== null && !!R.find(studyIds, (k) => k === studyId);
}
